import React, { useEffect, useState } from 'react';
import { EmptyData, LoadingScreen, Summary } from '@punchcard/core';
import { Bookmark, IconList } from '@punchcard/core/icons';
import classNames from 'classnames';
import documentAPI from 'api/documentsAPI';
import { useTranslation } from 'react-i18next';
import ResourcesDocument from './ResourcesDocument';
import Reader from '@punchcard/core/components/Courses/Reader';
import coreAPI from 'api/coreAPI';
import { useFormContext } from 'react-hook-form';

enum ResourceTab {
	SUMMARY = 0,
	RESOURCES = 1,
	READER = 2
}

interface IProps {
	course: TeacherCourseDTO | undefined;
	courseId: string | undefined;
	studentId: string | undefined;
	studentName: string | undefined;
	setCurrentActivity: (item: currentActivityAssessment) => boolean;
	safeStatuses: string[];
	activeUnit: number;
	setActiveUnit: (unit: number) => void;
	examReview: boolean;
	setExamReview: (value: boolean) => void;
	currentActivityItem: TeacherCourseActivityDTO | undefined;
	activeTab: number;
	setActiveTab: (tab: number) => void;
	handleWorkbookClick: () => void;
	handleWorkSampleToEDI?: () => void;
	setPendingAction?: (action: () => void) => void;
	setShowDiscardModal?: (show: boolean) => void;
}

function Resources(props: IProps) {
	const { course, currentActivityItem, studentName,
		setCurrentActivity, safeStatuses, activeUnit, setActiveUnit,
		examReview, setExamReview, activeTab, setActiveTab, handleWorkbookClick, handleWorkSampleToEDI,
		setPendingAction, setShowDiscardModal
	} = props;
	const { t } = useTranslation();
	const [loadingCourseDocument, setLoadingCourseDocument] = useState<boolean>(false);
	const [isWorkSampleFeatureFlagEnabled, setIsWorkSampleFeatureFlagEnabled] = useState<boolean>(false);
	const [activeLesson, setActiveLesson] = React.useState<null | number>(null);
	const [activeActivity, setActiveActivity] = React.useState<number | null>(null);
	const [openAccordions, setOpenAccordions] = React.useState<number[]>([]);
	const [featureFlagWorkSampleGeneratePdf, setFeatureFlagWorkSampleGeneratePdf] = useState<boolean>(false);
	const [finalGradeFeatureEnabled, setFinalGradeFeatureEnabled] = React.useState(false);
	const [newCalcFeatureEnabled, setNewCalcFeatureEnabled] = React.useState(false);
	const pba = currentActivityItem?.assessmentType && currentActivityItem?.assessmentType.find((type) => type.submittedDate !== undefined);
	const pdfExamData = React.useRef<File | null>(null);

	const wipForm = useFormContext();
	const { isDirty } = wipForm.formState;

	const handleTabClick = (index: number) => {
		if (isDirty && index !== activeTab) {
			setPendingAction?.(() => () => setActiveTab(index));
			setShowDiscardModal?.(true);
			return;
		}
		setActiveTab(index);
	};

	React.useEffect(() => {
		const fetchFeatureFlag = async () => {
			try {
				const response = await coreAPI.getFeatureFlag({ name: 'WorkSampleToEDI' });
				setIsWorkSampleFeatureFlagEnabled(response);
				setFinalGradeFeatureEnabled(await coreAPI.getFeatureFlag({ name: '57910_SendFinalGradeEDI'}));
				setNewCalcFeatureEnabled(await coreAPI.getFeatureFlag({ name: '59401_GradingService_Implementation'}));
			} catch (error) {
				console.error('Error fetching feature flag:', error);
			}
		};
		fetchFeatureFlag();
	}, []);

	const handleUnitClick = (index: number) => {
		// Check if this was triggered by a unit tab click
		const activeElement = document.activeElement as HTMLElement;
		const isUnitTabClick = activeElement?.id?.startsWith('unit-tab-');

		// Skip the dirty check for unit tab clicks - let the blocker handle it
		if (isDirty && !isUnitTabClick) {
			setPendingAction?.(() => () => {
				setActiveUnit(index);
			});
			setShowDiscardModal?.(true);
			return;
		}
		setActiveUnit(index);
	};

	const handleLessonClick = (lessonId: number) => {
		if (lessonId === activeLesson) {
			setActiveLesson(null);
			return;
		}
		setActiveLesson(lessonId);

		setOpenAccordions((prevlessonIds) =>
			prevlessonIds.includes(lessonId)
				? prevlessonIds.filter((i) => i !== lessonId)
				: [...prevlessonIds, lessonId]
		);
	};

	const handleActivityClick = (index: number | null, currentActivity: currentActivityAssessment, reAttemptNumber?: number) => {
		// Create activity object with attemptNumber included in the item if provided
		const updatedActivity: currentActivityAssessment = reAttemptNumber !== undefined
			? {
				...currentActivity,
				item: { ...currentActivity.item, reAttemptNumber }
			}
			: currentActivity;

		if (isDirty) {
			setPendingAction?.(() => () => {
				const canUpdateActivity = setCurrentActivity(updatedActivity);
				if (canUpdateActivity) {
					setActiveActivity(index);
				}
				if (examReview === true) {
					setExamReview(false);
				}
			});
			setShowDiscardModal?.(true);
			return;
		}
		const canUpdateActivity = setCurrentActivity(updatedActivity);
		if (canUpdateActivity) {
			setActiveActivity(index);
		}
		if (examReview === true) {
			setExamReview(false);
		}
	};

	// Sync activeActivity with currentActivity when URL navigation occurs
	useEffect(() => {
		if (currentActivityItem) {
			setActiveActivity(currentActivityItem.id);
		} else {
			setActiveActivity(null);
		}
	}, [currentActivityItem]);

	useEffect(() => {
		const lessons = course?.courseUnits[activeUnit]?.courseLessons;
		if (lessons) {
			setOpenAccordions(
				lessons
					.filter(lesson => lesson.courseLessonStatus?.statusCode !== 'Completed')
					.map(lesson => lesson.id)
			);
		}
	}, [activeUnit, course?.courseUnits]);

	useEffect(() => {
		if (examReview === true) {
			downloadPDF();
		} else {
			setActiveTab(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [examReview]);

	useEffect(() => {
		coreAPI.getFeatureFlag({ name: 'WorkSamplePDFGeneration' }).then((res) => {
			setFeatureFlagWorkSampleGeneratePdf(res);
		});
	}, []);

	const downloadPDF = async () => {
		try {
			setLoadingCourseDocument(true);
			if (pba !== undefined) {
				documentAPI.getCourseDocumentById(pba.assessmentDocumentId)
					.then(async (res) => {

						const response = await fetch(res);
						if (!response.ok) {
							throw new Error('error fetching document');
						}
						const blob = await response.blob();
						const document = new File([blob], 'document.pdf', { type: 'application/pdf' });
						pdfExamData.current = document;
						setActiveTab(2);
					});
			}
			setLoadingCourseDocument(false);
		} catch (error) {
			console.error('Error fetching documents:', error);
			setLoadingCourseDocument(false);
		}
	};

	const handleDownloadClick = async (workSampleFilename: string) => {
		try {
			const encodedFilename = encodeURIComponent(workSampleFilename);
			const fileUrl = await documentAPI.getFileWorkSample(encodedFilename);
			if (fileUrl) {
				const link = document.createElement('a');
				link.href = fileUrl;
				link.setAttribute('download', workSampleFilename);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(fileUrl);
			}
		} catch (error) {
			console.error('Error downloading file:', error);
		}
	};

	const renderTab = () => {
		if (loadingCourseDocument) {
			return <LoadingScreen />;
		}

		if (activeTab === ResourceTab.SUMMARY && course !== undefined) {
			return <Summary
				openAccordions={openAccordions}
				isWorkSampleFeatureFlagEnabled={isWorkSampleFeatureFlagEnabled}
				studentName={studentName}
				studentId={props.studentId}
				course={course}
				activeUnit={activeUnit}
				handleUnitClick={handleUnitClick}
				handleLessonClick={handleLessonClick}
				activeActivity={activeActivity}
				handleActivityClick={handleActivityClick}
				safeStatuses={safeStatuses}
				handleWorkbookClick={handleWorkbookClick}
				handleDownloadClick={handleDownloadClick}
				featureFlagWorkSampleGeneratePdf={featureFlagWorkSampleGeneratePdf}
				handleWorkSampleToEDI={handleWorkSampleToEDI}
				setPendingAction={setPendingAction}
				setShowDiscardModal={setShowDiscardModal}
				isDirty={isDirty}
				newCalcEnabled={newCalcFeatureEnabled}
				sendFinalGradeEnabled={finalGradeFeatureEnabled}
				currentActivityItem={currentActivityItem}
			/>;
		}

		if (activeTab === ResourceTab.RESOURCES) {
			return <ResourcesDocument
				course={course}
			/>;
		}

		if (activeTab === ResourceTab.READER) {
			return (
				<div className="overflow-y-auto">
					{currentActivityItem?.activityName !== undefined && <div className="d-flex">
						<span
							className={'no-action-tab ms-3 mt-2'}
							id={`tab-${2}`}
						>
							{currentActivityItem?.activityName}
						</span>
					</div>
					}
					<div className="flex-grow-1 overflow-y-auto" >
						<Reader pdfData={pdfExamData.current} />
					</div>
				</div>
			);
		}

		return (
			<EmptyData
				emptymessage={t('wip.no_resources_for_this_unit')}
				descriptionmessage={t('wip.description_no_resources_for_this_unit')}
				image="empty-resources"
				widthdescription="w-75"
			/>
		);
	};

	return (
		<div className="wrapper scroll-container-wrapper d-flex flex-column flex-grow-1 overflow-y-auto" aria-labelledby="resources component" >
			<header id="resource-header" className="resource-header">
				<div className="resource-tabs" id="resource-tabs">
					{
						<React.Fragment>
							{examReview === true && <div className="position-relative d-flex">
								<button
									className={classNames('resource-tab', { active: ResourceTab.READER == activeTab })}
									onClick={() => handleTabClick(2)}
									aria-selected={ResourceTab.READER === activeTab}
									aria-controls={'resource-tab-reader'}
									id={`tab-${2}`}
									role="tab"
								>
									{pba?.assessmentTypeCode === 'exam' ? t('assessment.exam') : t('assessment.alt_assessment')}
								</button>
								{ResourceTab.READER == activeTab && <Bookmark className="position-absolute bookmark-position" aria-hidden="true" />}
							</div>}
							<div className="position-relative d-flex">
								<button
									className={classNames('resource-tab', { active: ResourceTab.SUMMARY == activeTab })}
									onClick={() => setActiveTab(ResourceTab.SUMMARY)}
									aria-selected={ResourceTab.SUMMARY === activeTab}
									aria-controls={'resource-tab-summary'}
									id={`tab-${0}`}
									role="tab"
								>
									<IconList />
								</button>
								{ResourceTab.SUMMARY == activeTab && <Bookmark className="position-absolute bookmark-position" aria-hidden="true" />}
							</div>
							<div className="position-relative d-flex">
								<button
									className={classNames('resource-tab', { active: ResourceTab.RESOURCES == activeTab })}
									onClick={() => handleTabClick(1)}
									aria-selected={ResourceTab.RESOURCES === activeTab}
									aria-controls={'resource-tab-resources'}
									id={`tab-${1}`}
									role="tab"
								>
									{t('wip.resources')}
								</button>
								{ResourceTab.RESOURCES == activeTab && <Bookmark className="position-absolute bookmark-position" aria-hidden="true" />}
							</div>
						</React.Fragment>
					}
				</div>
			</header>
			{renderTab()}
		</div>
	);
}

export default Resources;